"use client";

import { Download, Loader2, Mail, ShoppingCart } from "lucide-react";

interface Props {
  /** One short sentence under the title. */
  body: string;
  /** Omit to hide the purchase button (nothing in the design is sellable). */
  purchaseLabel?: string;
  onPurchase?: () => void;
  purchasing?: boolean;
  /**
   * Show the purchase button greyed out instead of hiding it (container flow:
   * nothing in the design can ship from this store). `purchaseNote` explains why.
   */
  purchaseDisabled?: boolean;
  purchaseNote?: string;
  onEmail: () => void;
  emailing?: boolean;
  /** Omit to hide the "Download as PDF" link. */
  onPdf?: () => void;
  downloading?: boolean;
}

const BUTTON =
  "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-center text-[15px] font-bold leading-tight transition disabled:cursor-not-allowed disabled:opacity-60";

/**
 * The "Make it Happen" box at the bottom of both result pages (outdoor and
 * container): buy it, or email it. Two equal one-line buttons, side by side
 * from `sm` up and stacked full width on a phone.
 */
export function MakeItHappen({
  body,
  purchaseLabel,
  onPurchase,
  purchasing,
  purchaseDisabled = false,
  purchaseNote,
  onEmail,
  emailing,
  onPdf,
  downloading,
}: Props) {
  const canPurchase = Boolean(purchaseLabel && onPurchase);
  return (
    <section className="mx-auto w-full max-w-2xl rounded-2xl border-2 border-plantista-950 bg-white p-5 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:border-plantista-50 dark:bg-plantista-900 dark:shadow-plantista-50 sm:p-6">
      <h3 className="font-display text-xl font-bold text-plantista-950 dark:text-white">
        Make it Happen
      </h3>
      <p className="mt-1 text-sm text-plantista-600 dark:text-plantista-300">
        {body}
      </p>

      <div className={`mt-5 grid gap-3 ${canPurchase ? "sm:grid-cols-2" : ""}`}>
        {canPurchase && (
          <button
            type="button"
            onClick={onPurchase}
            disabled={purchasing || purchaseDisabled}
            aria-describedby={
              purchaseNote ? "make-it-happen-purchase-note" : undefined
            }
            className={`${BUTTON} bg-plantista-500 text-plantista-950 hover:bg-plantista-600 disabled:grayscale disabled:hover:bg-plantista-500 dark:bg-plantista-400 dark:hover:bg-plantista-300 dark:disabled:hover:bg-plantista-400`}
          >
            {purchasing ? (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
            ) : (
              <ShoppingCart className="h-5 w-5 shrink-0" />
            )}
            {purchaseLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onEmail}
          disabled={emailing}
          className={`${BUTTON} border border-gray-300 bg-white text-plantista-950 hover:bg-gray-50 dark:border-plantista-700 dark:bg-plantista-800 dark:text-plantista-100 dark:hover:bg-plantista-700`}
        >
          {emailing ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
          ) : (
            <Mail className="h-5 w-5 shrink-0" />
          )}
          Email me the design
        </button>
      </div>

      {canPurchase && purchaseNote && (
        <p
          id="make-it-happen-purchase-note"
          className="mt-3 text-center text-sm text-plantista-600 dark:text-plantista-300"
        >
          {purchaseNote}
        </p>
      )}

      {onPdf && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-plantista-700 hover:text-plantista-950 disabled:opacity-60 dark:text-plantista-300 dark:hover:text-white"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download as PDF
          </button>
        </div>
      )}
    </section>
  );
}
